import BreadCrumbs from 'components/breadcrumbs'
import React, { useEffect, useState } from 'react'
import { Card, CardBody } from 'reactstrap'
import { TableComponent as DataTable } from 'components/TableComponent'
import { formatDatePMB } from 'utils/formatDate'
import { ChevronDown, FileText, User } from 'react-feather'
import { useSelector } from 'react-redux'
import PMBProfilePic from 'components/PMBProfilePic'
import { useIntl } from 'react-intl'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import ICTunai from 'assets/icons/currency-dollar.svg'
import ICUangBelanja from 'assets/icons/uang-belanja-wallet.png'
import ICTalangin from 'assets/icons/uang-usaha.png'
import { useNavigate } from 'react-router-dom'
import PMBPagination from 'components/PMBPagination'
import LoadingAnimation from 'components/LoadingAnimation'
import { URL_FULFILMENT_REPORT } from 'utils/pathUrl'
import LihatDetail from 'components/Icons/LihatDetail'
import useGetTrxDashboard from 'hooks/useGetTrxDashboard'
import SvgIcon from 'components/Icon/SvgIcon'

const ReportFulfilment = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { isOpenSidebarFilter, tag, ownerData } = useSelector(
    (s) => s.dashboard,
  )
  const [size, setSize] = useState(10)
  const [sortdir, setSortdir] = useState('desc')
  const [sortby, setSortby] = useState('createdAt')
  const { serviceProvision } = useSelector((s) => s.auth)

  const [page, setPage] = useState(() => {
    const storedPage = localStorage.getItem('pageReportFulfilment')
    return storedPage ? Number(storedPage) : 0
  })

  useEffect(() => {
    localStorage.setItem('pageReportFulfilment', Number(page))
  }, [page])

  const { data, isLoading, error, isFetching, refetch } = useGetTrxDashboard(
    {
      type: 'SELL',
      status: 'Menunggu Proses Penjual',
      size,
      page,
      sortdir,
      sortby,
      forSellerProcessing: true,
      from: '',
      to: '',
    },
    {
      enabled: serviceProvision?.sellerProcessing === true,
    },
  )

  useEffect(() => {
    if (serviceProvision?.sellerProcessing) {
      refetch()
    }
  }, [page, size, sortdir, sortby, serviceProvision?.sellerProcessing])

  const columns = [
    {
      name: <span>Tanggal transaksi</span>,
      cell: (row) => {
        const date = row?.trxDate || row?.createdAt
        return date ? formatDatePMB(date, date) : '-'
      },
      sortable: true,
      sortField: 'createdAt',
      minWidth: '150px',
    },
    {
      name: <span>Nomor transaksi</span>,
      cell: (row) => (
        <div data-testid={`fulfilment-table-row-${row?.id}`}>
          {row?.trxNumber || row?.orderDetail?.transaction_number || row?.orderDetail?.poNumber || '-'}
        </div>
      ),
      minWidth: '150px',
    },
    {
      name: <span>Pembeli</span>,
      cell: (row) => {
        const picName = row?.buyerDetail?.picName || row?.picBuyerDetail?.name
        const mainName = row?.buyerDetail?.name || row?.buyerDetail?.fullName
        const displayName = picName || mainName || '-'
        const avatarSrc = row?.buyerDetail?.profilePhoto || row?.buyerDetail?.picProfilePhoto || row?.sellerOrBuyAvatar

        return (
          <div className="d-flex flex-column my-2">
            <>
              <div className="d-flex gap-2">
                <PMBProfilePic
                  imgSrc={avatarSrc}
                  altText={displayName}
                  name={displayName}
                  avatarSeed={row?.buyerDetail?.userId}
                  width={20}
                  height={20}
                />
                <span>{displayName}</span>
              </div>
              {row.buyerDetail?.partnership?.name && (
                <div
                  className="badge rounded-pill text-primary bg-gray-100 font-semibold mt-2"
                  style={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    textAlign: 'center',
                    maxWidth: '100%',
                  }}
                >
                  {row?.buyerDetail?.partnership?.name ?? ''}
                </div>
              )}
            </>
          </div>
        )
      },
      minWidth: '150px',
    },
    {
      name: <span>Barang</span>,
      cell: (row) => (
        <div className="d-flex flex-column py-2">
          <span className="flex-row">{row?.itemDetail?.[0]?.nameItem}</span>
          <span className="text-primary">
            {intl.formatNumber(row?.itemDetail?.[0]?.amount, {
              useGrouping: 'always',
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}{' '}
            {row?.itemDetail?.[0]?.unit} x Rp
            {intl.formatNumber(row?.itemDetail?.[0]?.price, {
              useGrouping: 'always',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </span>
          {row?.totalItemDetail > 1 ? (
            <div className="bg-gray-100 badge rounded-pill my-1">
              <span className="text-dark fw-medium">
                +{' '}
                {intl.formatNumber(row?.totalItemDetail - 1, {
                  useGrouping: 'always',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}{' '}
                barang lainnya
              </span>
            </div>
          ) : (
            ''
          )}
        </div>
      ),
      minWidth: '150px',
    },
    {
      name: (
        <div
          style={{
            textAlign: 'right',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          Total Transaksi
        </div>
      ),
      cell: (row) => (
        <div className="d-flex justify-content-end w-100">
          <span>Rp{newFormatThousandSeparator(row?.totalAmount, 0)}</span>
        </div>
      ),
      sortable: true,
      sortField: 'totalAmount',
      minWidth: '150px',
    },
    {
      name: <span>Status</span>,
      minWidth: '100px',
      cell: (row) => {
        if (row.status?.toLowerCase() === 'menunggu proses penjual') {
          return (
            <div
              className="badge rounded-pill bg-warning-50 text-warning-700 px-2"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'center',
                maxWidth: '100%',
              }}
            >
              {row.status}
            </div>
          )
        }
      },
    },
    // {
    //   name: <span>Metode pembayaran</span>,
    //   minWidth: '150px',
    //   grow: 2,
    //   cell: (row) => {
    //     if (
    //       (row?.orderDetail?.useProofOfpayment ||
    //         row?.orderDetail?.useProofOfPayment) &&
    //       row?.orderDetail?.paymentTime
    //     ) {
    //       return (
    //         <div
    //           className={`badge rounded-pill bg-purple-50 text-purple-700 px-2 py-1 flex-centered gap-1`}
    //           style={{
    //             whiteSpace: 'normal',
    //             wordBreak: 'break-word',
    //             maxWidth: '100%',
    //           }}
    //         >
    //           <SvgIcon
    //             name="old-icons-hand-holding-usd"
    //             size={16}
    //             color="white"
    //             withBackground={true}
    //             backgroundColor="#7F56D9"
    //           />{' '}
    //           <span> COD</span>
    //         </div>
    //       )
    //     }
    //     if (row?.orderDetail?.usePayable || row?.escrowpayment?.usePayable) {
    //       return (
    //         <div
    //           className={`badge rounded-pill bg-primary-50 px-2 py-1 flex-centered gap-1`}
    //           style={{
    //             whiteSpace: 'normal',
    //             wordBreak: 'break-word',
    //             maxWidth: '100%',
    //           }}
    //         >
    //           <SvgIcon
    //             name="database"
    //             size={16}
    //             color="white"
    //             withBackground={true}
    //             backgroundColor="#039855"
    //           />
    //           <span className="text-black">Kredit Penjual</span>
    //         </div>
    //       )
    //     }
    //     if (
    //       row?.orderDetail?.useProofOfpayment ||
    //       row?.orderDetail?.useProofOfPayment ||
    //       row?.totalAmount === 0
    //     ) {
    //       return (
    //         <div
    //           className="badge rounded-pill bg-gray-100 text-gray-700 px-2 py-1 flex-centered gap-1"
    //           style={{
    //             whiteSpace: 'normal',
    //             wordBreak: 'break-word',
    //             maxWidth: '100%',
    //           }}
    //         >
    //           <img width={18} height={18} src={ICTunai} alt={'Tunai'} /> Tunai
    //         </div>
    //       )
    //     }
    //     if (
    //       row?.sellerDetail?.disbursementViaAgent === true ||
    //       row?.sellerDetail?.disbursementViaAgent === 'true'
    //     ) {
    //       return (
    //         <div className="d-flex flex-column gap-1 py-1">
    //           <div
    //             className="badge rounded-pill text-primary-v2 bg-primary-50 px-2 py-1 flex-centered gap-1"
    //             style={{
    //               whiteSpace: 'normal',
    //               wordBreak: 'break-word',
    //               maxWidth: '100%',
    //             }}
    //           >
    //             <User size={14} /> Pembayaran Melalui Agen
    //           </div>
    //           <div
    //             className={`badge rounded-pill bg-primary-50 px-2 py-1 flex-centered gap-1`}
    //             style={{
    //               whiteSpace: 'normal',
    //               wordBreak: 'break-word',
    //               maxWidth: '100%',
    //             }}
    //           >
    //             <img
    //               width={16}
    //               height={16}
    //               src={ICUangBelanja}
    //               alt={'Non Tunai'}
    //             />
    //             <span className={'text-primary'}>Non Tunai</span>
    //           </div>
    //         </div>
    //       )
    //     }
    //     if (!row?.creditRequested) {
    //       return (
    //         <div
    //           className={`badge rounded-pill bg-primary-50 px-2 py-1 flex-centered gap-1`}
    //           style={{
    //             whiteSpace: 'normal',
    //             wordBreak: 'break-word',
    //             maxWidth: '100%',
    //           }}
    //         >
    //           <img
    //             width={16}
    //             height={16}
    //             src={ICUangBelanja}
    //             alt={'Non Tunai'}
    //           />
    //           <span className={'text-primary'}>Non Tunai</span>
    //         </div>
    //       )
    //     }
    //     if (row?.creditRequested) {
    //       return (
    //         <div
    //           className={`badge rounded-pill bg-primary-50 px-2 py-1 flex-centered gap-1`}
    //           style={{
    //             whiteSpace: 'normal',
    //             wordBreak: 'break-word',
    //             maxWidth: '100%',
    //           }}
    //         >
    //           <img
    //             width={16}
    //             height={16}
    //             src={ICUangBelanja}
    //             alt={'Non Tunai'}
    //           />
    //           <span className={'text-primary'}>Non Tunai</span>
    //         </div>
    //       )
    //     }
    //   },
    // },
    ...(ownerData?.status === 'VERIFIED'
      ? [
          {
            name: '',
            minWidth: '100px',
            selector: (row) => {
              const detailId = row?.escrowpaymentId || row?.escrowPaymentId || row?.tradeshortcutId || row?.tradeShortcutId || row?.id
              return (
              <div data-testid={`fulfilment-row-detail-${row?.id}`}>
                <LihatDetail
                  onClick={() => {
                    navigate(`${URL_FULFILMENT_REPORT}/${detailId}`, {
                      state: { rowData: row },
                    })
                  }}
                  id={row?.id}
                />
              </div>)
            },
            center: true,
          },
        ]
      : []),
  ]

  const handleSort = (column, direction) => {
    setSortdir(direction)
    setSortby(column.sortField)
  }

  const renderMobileCard = (row, index) => {
    const trxNumber =
      row?.trxNumber || row?.orderDetail?.transaction_number || row?.orderDetail?.poNumber || '-'
    const date = row?.trxDate || row?.createdAt
    const trxDate = date ? formatDatePMB(date, date) : '-'
    const picName = row?.buyerDetail?.picName || row?.picBuyerDetail?.name
    const mainName = row?.buyerDetail?.name || row?.buyerDetail?.fullName
    const buyerName = picName || mainName || '-'
    const partnershipName = row?.buyerDetail?.partnership?.name

    const itemDetail = row?.itemDetail?.[0]
    const amountFormatted = itemDetail?.amount
      ? intl.formatNumber(itemDetail?.amount, {
          useGrouping: 'always',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      : '0'
    const priceFormatted = itemDetail?.price
      ? intl.formatNumber(itemDetail?.price, {
          useGrouping: 'always',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      : '0'

    const totalAmountFormatted = newFormatThousandSeparator(row?.totalAmount, 0)

    return (
      <Card
        key={row?.id || index}
        className="mb-3 border-0 shadow-sm overflow-hidden"
        style={{ borderRadius: '12px' }}
      >
        <CardBody className="p-0">
          {/* Header Section */}
          <div
            className="p-3 border-bottom d-flex justify-content-between align-items-start"
            style={{ backgroundColor: '#F9FAFB' }}
          >
            <div>
              <div
                className="text-primary fw-bold mb-1"
                style={{ fontSize: '0.85rem' }}
              >
                {trxNumber}
              </div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                {trxDate}
              </div>
            </div>
            {row?.status?.toLowerCase() === 'menunggu proses penjual' && (
              <div
                className="badge rounded-pill bg-warning-50 text-warning-700 px-2 py-1 fw-semibold"
                style={{ fontSize: '0.75rem' }}
              >
                {row?.status}
              </div>
            )}
          </div>

          {/* Body Section */}
          <div className="p-3">
            {/* Buyer Profile */}
            <div className="d-flex align-items-center mb-3">
              <PMBProfilePic
                imgSrc={row?.buyerDetail?.profilePhoto || row?.buyerDetail?.picProfilePhoto || row?.sellerOrBuyAvatar}
                altText={buyerName}
                width={36}
                height={36}
              />
              <div className="ms-2 flex-grow-1">
                <div
                  className="fw-bold text-dark"
                  style={{ fontSize: '0.9rem', lineHeight: '1.3' }}
                >
                  {buyerName}
                </div>
                {partnershipName && (
                  <div
                    className="badge bg-gray-100 text-primary rounded-pill mt-1"
                    style={{ fontSize: '0.7rem', fontWeight: 600 }}
                  >
                    {partnershipName}
                  </div>
                )}
              </div>
            </div>

            {/* Commodity Info Box */}
            <div
              className="p-2 px-3 rounded mb-3"
              style={{
                backgroundColor: '#F9FAFB',
                borderLeft: '3px solid #007798',
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div className="fw-semibold text-dark" style={{ fontSize: '0.85rem' }}>
                  {itemDetail?.nameItem || '-'}
                </div>
                <div className="text-primary fw-bold" style={{ fontSize: '0.8rem' }}>
                  {amountFormatted} {itemDetail?.unit || ''} x Rp{priceFormatted}
                </div>
              </div>
              {row?.totalItemDetail > 1 && (
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  + {row.totalItemDetail - 1} barang lainnya
                </div>
              )}
            </div>

            {/* Total & Action Footer */}
            <div className="d-flex justify-content-between align-items-center pt-2 border-top">
              <div>
                <div
                  className="text-muted"
                  style={{
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                  }}
                >
                  Total Transaksi
                </div>
                <div className="fw-bold text-dark" style={{ fontSize: '1rem' }}>
                  Rp{totalAmountFormatted}
                </div>
              </div>

              {ownerData?.status === 'VERIFIED' && (
                <LihatDetail
                  onClick={() => {
                    const detailId = row?.escrowpaymentId || row?.escrowPaymentId || row?.tradeshortcutId || row?.tradeShortcutId || row?.id
                    navigate(`${URL_FULFILMENT_REPORT}/${detailId}`, {
                      state: { rowData: row },
                    })
                  }}
                  id={row?.id}
                />
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <div className="w-100 px-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'Pemenuhan Order'} />
      </div>
      <div className="d-flex flex-column gap-2 mb-2">
        <Card className="border-0 shadow-sm">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 size="large">Pemenuhan Order</h5>
            </div>
            <DataTable
              sortServer
              defaultSortFieldId={1}
              defaultSortAsc={false}
              sortIcon={<ChevronDown />}
              onSort={handleSort}
              columns={columns}
              data={data?.items}
              progressPending={isFetching || isLoading}
              renderMobileCard={renderMobileCard}
              pagination
              paginationServer
              paginationComponent={(props) => {
                return (
                  <PMBPagination
                    rowCount={data?.totalItems || 0}
                    currentPage={page}
                    rowsPerPage={size}
                    setCurrentPage={(e) => {
                      setPage(e)
                    }}
                    setRowsPerPage={(e) => {
                      setSize(e)
                    }}
                    props={props}
                  />
                )
              }}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default ReportFulfilment
